import { zodResolver } from '@hookform/resolvers/zod';
import dompurify from 'dompurify';
import { useForm, useFormContext } from 'react-hook-form';
import { z } from 'zod';
import { useGenerateOutreach, useOutreachEmailTemplate } from '../../../../api/useOutreachCandidate';
import UIStrings from '@/Utils/UIStrings';
import ValidationErrors from '@/Utils/ValidationErrors';

const emailCandidateFormSchema = z.object({
    emailHtml: z.string().min(1),
    emailHtmlTextLength: z
        .number()
        .positive(ValidationErrors.Required)
        .max(5000, ValidationErrors.PleaseLimitDescriptionError),
    subject: z
        .string()
        .min(1, ValidationErrors.Required)
        .max(500, { error: ({ maximum }) => UIStrings.MaxSymbolsValidationError(maximum) }),
});

export type OutreachCandidateEmailTemplateForm = z.infer<typeof emailCandidateFormSchema>;

export const useEmailCandidateForm = () => {
    // this should be the only place to handle ai generated outreach
    // unless if you are using useSWR/useSWRImmut
    const { trigger } = useGenerateOutreach();
    const { data: savedTemplate } = useOutreachEmailTemplate();

    return useForm<OutreachCandidateEmailTemplateForm>({
        resolver: zodResolver(emailCandidateFormSchema),
        mode: 'all',
        defaultValues: async () => {
            const defaultValues: OutreachCandidateEmailTemplateForm = {
                emailHtml: '',
                emailHtmlTextLength: 0,
                subject: '',
            };

            if (savedTemplate) {
                const parser = new DOMParser();
                const dom = parser.parseFromString(savedTemplate.templateHtml, 'text/html');

                defaultValues.emailHtml = dompurify.sanitize(savedTemplate.templateHtml);
                defaultValues.emailHtmlTextLength = dom.body?.textContent?.length || 0;
                defaultValues.subject = savedTemplate.subject;
            } else {
                try {
                    const generatedOutreach = await trigger();
                    const emailHtml = outreachEmailMessageBodyToHtml(generatedOutreach.emailMessageBody);

                    defaultValues.emailHtml = dompurify.sanitize(emailHtml);
                    defaultValues.emailHtmlTextLength = generatedOutreach.emailMessageBody.length;
                    defaultValues.subject = generatedOutreach.emailSubject;
                } catch {
                    return defaultValues;
                }
            }

            return defaultValues;
        },
    });
};

export const useEmailCandidateFormContext = () => useFormContext<OutreachCandidateEmailTemplateForm>();

// only use for outreach email generated by AI
const outreachEmailMessageBodyToHtml = (emailMessageBody: string) => {
    const nodes = emailMessageBody.split(/\n/g);
    const encodedEmailMessageBody = nodes
        // fully encode to html paragraphs
        .map((node) => `<p>${node}</p>`)
        // transform your nodes here
        .map((node, i, arr) => {
            const listSearchValue = '<p>- ';

            // transform node starting with dash to unordered list
            if (node.startsWith(listSearchValue)) {
                const newNode = `<li>${node.replace(listSearchValue, '<p>')}</li>`;

                if (!arr[i - 1].startsWith(listSearchValue)) {
                    return `<ul>${newNode}`;
                } else if (!arr[i + 1].startsWith(listSearchValue)) {
                    return `${newNode}</ul>`;
                } else {
                    return newNode;
                }
            } else {
                return node;
            }
        })
        .join('');

    return encodedEmailMessageBody;
};
